// Stage 2 — embed the mission graph in continuous space. Force-directed
// relaxation: edges are springs at a rest length (room radii + a hall's
// worth of travel), all node pairs repel until clear of each other's
// footprints. Amoeba has no packing constraints (infinite cheap water), so
// ~250 cooled iterations settle every graph the grammar can produce.
//
// Deterministic: seeded ring init in cycle order (which starts the loop
// nearly untangled), fixed iteration count, no wall-clock anywhere.

import { mulberry32 } from '../rng'
import { T } from '../tunables'
import type { DunGraph, Vec2 } from './types'

/** The subset of DunGraph both embedders need — the region web (B2) is not
 *  a mission cycle, so relaxPositions can't assume entrance/arcs exist. */
export interface GraphLike {
  nodes: DunGraph['nodes']
  edges: DunGraph['edges']
}

/** Main-disc radius for a node — shared with carve so spacing and geometry agree. */
export function roomRadius(sizeHint: number): number {
  return T.dungeon.roomBaseR * sizeHint
}

function restLength(graph: GraphLike, e: { a: number; b: number; restMult?: number }): number {
  return (
    roomRadius(graph.nodes[e.a].sizeHint) +
    roomRadius(graph.nodes[e.b].sizeHint) +
    T.dungeon.hallLength * (e.restMult ?? 1)
  )
}

export function embedGraph(graph: DunGraph): Vec2[] {
  // Crossed halls carve junctions the graph doesn't know about — extra
  // loops are harmless today but would let a player swim around a lock
  // (B4). Retry with a rotated/re-jittered init until crossing-free,
  // keeping the least-crossed attempt as a fallback. Deterministic: the
  // attempt index feeds the seed.
  let best: Vec2[] | null = null
  let bestCrossings = Infinity
  for (let attempt = 0; attempt < 8; attempt++) {
    const pos = embedOnce(graph, attempt)
    const crossings = countEdgeCrossings(graph, pos)
    if (crossings < bestCrossings) {
      best = pos
      bestCrossings = crossings
    }
    if (crossings === 0) break
  }
  return best!
}

function embedOnce(graph: DunGraph, attempt: number): Vec2[] {
  const rng = mulberry32((graph.seed ^ 0x9e3779b9 ^ (attempt * 0x85ebca6b)) >>> 0)
  const n = graph.nodes.length
  const pos: Vec2[] = new Array(n)

  // Ring init in cycle order: entrance → arc A → goal → arc B (reversed)
  // around a circle sized to the cycle's total rest length.
  const ring = [graph.entrance, ...graph.arcA, graph.goal, ...[...graph.arcB].reverse()]
  let perim = 0
  for (let i = 0; i < ring.length; i++) {
    perim += restLength(graph, { a: ring[i], b: ring[(i + 1) % ring.length] })
  }
  const ringR = perim / (2 * Math.PI)
  for (let i = 0; i < ring.length; i++) {
    const ang = (i / ring.length) * Math.PI * 2 + (rng() - 0.5) * 0.25
    pos[ring[i]] = { x: Math.cos(ang) * ringR, y: Math.sin(ang) * ringR }
  }
  // Off-cycle nodes (sub-loops, pockets) start jittered near any placed
  // neighbor; graph order guarantees hosts were created (and placed) first.
  for (const node of graph.nodes) {
    if (pos[node.id]) continue
    const anchor = graph.edges.find(e => (e.a === node.id && pos[e.b]) || (e.b === node.id && pos[e.a]))
    const ap = anchor ? pos[anchor.a === node.id ? anchor.b : anchor.a] : { x: 0, y: 0 }
    const ang = rng() * Math.PI * 2
    const d = 1.0 + rng() * 1.5
    pos[node.id] = { x: ap.x + Math.cos(ang) * d, y: ap.y + Math.sin(ang) * d }
  }

  relaxPositions(graph, pos, rng)

  // Normalize: entrance at the local origin. generateDungeon() translates
  // the whole layout to its world-space origin afterwards.
  const e0 = pos[graph.entrance]
  const ox = e0.x
  const oy = e0.y
  for (const p of pos) {
    p.x -= ox
    p.y -= oy
  }
  return pos
}

/** Force relaxation shared by the mission embedder and the world-region
 *  embedder (B2). Springs toward rest on edges; linear push-apart on any
 *  pair closer than clearance (footprints + pad); cooled steps. `extraForce`
 *  lets a caller add a per-node prior (region: radial band pull); `pin`
 *  holds one node fixed (region: the hub at the origin). */
export function relaxPositions(
  graph: GraphLike,
  pos: Vec2[],
  rng: () => number,
  opts: { extraForce?: (i: number, out: { x: number; y: number }) => void; pin?: number } = {},
): void {
  const n = graph.nodes.length
  const iters = Math.round(T.dungeon.embedIterations)
  const fx = new Float64Array(n)
  const fy = new Float64Array(n)
  const extra = { x: 0, y: 0 }
  for (let it = 0; it < iters; it++) {
    const alpha = 0.16 * (1 - it / iters) + 0.02
    fx.fill(0)
    fy.fill(0)

    for (const e of graph.edges) {
      const pa = pos[e.a]
      const pb = pos[e.b]
      let dx = pb.x - pa.x
      let dy = pb.y - pa.y
      let d = Math.hypot(dx, dy)
      if (d < 1e-6) {
        // Coincident nodes: nudge apart along a seeded direction.
        const ang = rng() * Math.PI * 2
        dx = Math.cos(ang)
        dy = Math.sin(ang)
        d = 1
      }
      // Asymmetric spring: pull hard when overstretched (long thin halls
      // are the corridor-tedium trap), push gently when compressed (rooms
      // nestling close is what porous banks want).
      const rest = restLength(graph, e)
      const stretch = (d - rest) * (d > rest ? 0.85 : 0.5)
      const ux = dx / d
      const uy = dy / d
      fx[e.a] += ux * stretch
      fy[e.a] += uy * stretch
      fx[e.b] -= ux * stretch
      fy[e.b] -= uy * stretch
    }

    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        const dx = pos[j].x - pos[i].x
        const dy = pos[j].y - pos[i].y
        const d = Math.hypot(dx, dy) || 1e-6
        const clearance =
          roomRadius(graph.nodes[i].sizeHint) +
          roomRadius(graph.nodes[j].sizeHint) +
          T.dungeon.repelPad
        if (d >= clearance) continue
        const push = (clearance - d) * 0.6
        const ux = dx / d
        const uy = dy / d
        fx[i] -= ux * push
        fy[i] -= uy * push
        fx[j] += ux * push
        fy[j] += uy * push
      }
    }

    if (opts.extraForce) {
      for (let i = 0; i < n; i++) {
        extra.x = 0
        extra.y = 0
        opts.extraForce(i, extra)
        fx[i] += extra.x
        fy[i] += extra.y
      }
    }

    for (let i = 0; i < n; i++) {
      if (i === opts.pin) continue
      pos[i].x += fx[i] * alpha
      pos[i].y += fy[i] * alpha
    }
  }
}

/** Number of non-incident edge pairs whose segments intersect. */
export function countEdgeCrossings(graph: GraphLike, pos: Vec2[]): number {
  let n = 0
  for (let i = 0; i < graph.edges.length; i++) {
    for (let j = i + 1; j < graph.edges.length; j++) {
      const e = graph.edges[i]
      const f = graph.edges[j]
      if (e.a === f.a || e.a === f.b || e.b === f.a || e.b === f.b) continue
      if (segmentsIntersect(pos[e.a], pos[e.b], pos[f.a], pos[f.b])) n++
    }
  }
  return n
}

export function segmentsIntersect(p1: Vec2, p2: Vec2, p3: Vec2, p4: Vec2): boolean {
  const d = (a: Vec2, b: Vec2, c: Vec2) => (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x)
  const d1 = d(p3, p4, p1)
  const d2 = d(p3, p4, p2)
  const d3 = d(p1, p2, p3)
  const d4 = d(p1, p2, p4)
  return ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))
}
